import {
  Box,
  Collapse,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  type Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { DateTime } from "luxon";
import { Column } from "../Column";
import { Row } from "../Row";
import { WorktimeStatsDto2 } from "../../api/server.api";
import { useInstructorCalendarEventsReport } from "../../hooks/useInstructorCalendarEventsReport";
import { useInstructorWorktimeAggregationReport } from "../../hooks/useInstructorWorktimeAggregationReport";
import { DateRange } from "../../model/DateRange";
import { useMenu } from "../../hooks/useMenu";
import { CreateReportDialog, type Report } from "../../dialogs/CreateReportDialog";
import { useDialog } from "../../hooks/useDialog";
import { useState } from "react";
import { instructorCalendarViewLabels, type InstructorCalendarView } from "./InstructorCalendarHeaderMenuButton";

const _numberFormatter = new Intl.NumberFormat("de", { maximumFractionDigits: 2 });

export function InstructorCalendarStats(props: {
  worktimeStats: WorktimeStatsDto2 | null;
  dateRange: DateRange;
  calendarView: InstructorCalendarView;
}) {
  const { worktimeStats, dateRange, calendarView } = props;
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("sm"));
  const [showStats, setShowStats] = useState(false);
  const { anchorEl, open, openMenu, closeMenu } = useMenu();
  const calendarEventsReport = useInstructorCalendarEventsReport();
  const worktimeAggregationReport = useInstructorWorktimeAggregationReport();
  const { dialogProps: createReportDialogProps, openDialog: openCreateReportDialog } = useDialog();
  const [selectedReport, setSelectedReport] = useState<null | Report>(null);
  const viewData: Record<
    keyof WorktimeStatsDto2["data"],
    { label: string; unit: { singular: string; plural: string } }
  > = {
    drivingLessonMinutes: { label: "Fahrstunden", unit: { singular: "UE", plural: "UE" } },
    theoryLessonMinutes: { label: "Theorieunterricht", unit: { singular: "UE", plural: "UE" } },
    practicalExamMinutes: { label: "Prakt. Prüfungen", unit: { singular: "UE", plural: "UE" } },
    canceledAtShortNoticeMinutes: { label: "Kurzfr. abgesagt", unit: { singular: "UE", plural: "UE" } },
    daysAbsent: { label: "Abwesenheiten", unit: { singular: "Tag", plural: "Tage" } },
    publicHolidays: { label: "Feiertage", unit: { singular: "Tag", plural: "Tage" } },
    otherWorkMinutes: { label: "Sonstiges", unit: { singular: "Min.", plural: "Min." } },
  };

  if (!worktimeStats) {
    return null;
  }

  const lastAggregatedAt = worktimeStats.lastAggregatedAt ? DateTime.fromISO(worktimeStats.lastAggregatedAt) : null;
  const statsExpanded = isDesktop || showStats;
  return (
    <Box
      sx={{
        alignItems: "start",
        columnGap: { xs: 0.5, sm: 0 },
        display: "grid",
        gridTemplateColumns: { xs: "minmax(0, 1fr) auto auto", sm: "minmax(0, 1fr) auto" },
        minWidth: 0,
        rowGap: { xs: statsExpanded ? 1 : 0, sm: 0 },
        width: "100%",
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{ alignSelf: "center", display: { xs: "block", sm: "none" }, fontWeight: 600 }}
      >
        Statistik ({instructorCalendarViewLabels[calendarView]})
      </Typography>
      <Collapse
        in={statsExpanded}
        timeout="auto"
        unmountOnExit
        sx={{
          gridColumn: { xs: "1 / -1", sm: "1" },
          gridRow: { xs: "2", sm: "1" },
          minWidth: 0,
          width: "100%",
          "& .MuiCollapse-wrapperInner": { width: "100%" },
        }}
      >
        <Box
          id="instructor-calendar-stats-grid"
          data-testid="instructor-calendar-stats-grid"
          sx={{
            display: "grid",
            gap: { xs: 1, sm: 3, lg: "40px" },
            gridTemplateColumns: {
              xs: "repeat(2, minmax(0, 1fr))",
              sm: "repeat(auto-fit, minmax(104px, 1fr))",
            },
            minWidth: 0,
            pb: { xs: 1, sm: 0 },
          }}
        >
          {Object.entries(viewData).map(([type, { label, unit }]) => {
            let amount = worktimeStats.data
              ? (worktimeStats.data[type as keyof WorktimeStatsDto2["data"]] as number)
              : NaN;
            if (unit.singular === "UE") {
              amount /= 45;
            }
            return <State key={type} label={label} amount={amount} unit={unit} />;
          })}
        </Box>
      </Collapse>
      <IconButton
        onClick={openMenu}
        data-testid="worktime-options-menu-button"
        aria-label="Weitere Optionen"
        sx={{ gridColumn: "2", gridRow: "1" }}
      >
        <MoreVertIcon />
      </IconButton>
      <IconButton
        aria-controls="instructor-calendar-stats-grid"
        aria-expanded={statsExpanded}
        aria-label={statsExpanded ? "Statistik zuklappen" : "Statistik aufklappen"}
        data-testid="instructor-calendar-stats-toggle-button"
        onClick={() => setShowStats((visible) => !visible)}
        sx={{ display: { xs: "inline-flex", sm: "none" }, gridColumn: "3", gridRow: "1" }}
      >
        <ExpandMoreIcon
          sx={{ transform: statsExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 200ms" }}
        />
      </IconButton>
      <Menu
        key="moreMenu"
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ "aria-label": "Weitere Optionen" }}
      >
        {lastAggregatedAt && (
          <Row
            sx={{
              gap: "4px",
              justifyContent: "center",
              px: "16px",
              pb: "4px",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Zuletzt aktualisiert:
            </Typography>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">
              {lastAggregatedAt?.toFormat("dd.MM.yyyy HH:mm")} Uhr
            </Typography>
          </Row>
        )}
        <MenuItem
          onClick={() => {
            closeMenu();
            setSelectedReport(calendarEventsReport);
            openCreateReportDialog();
          }}
        >
          <ListItemText>{calendarEventsReport.localizedTitle} erstellen ...</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeMenu();
            setSelectedReport(worktimeAggregationReport);
            openCreateReportDialog();
          }}
        >
          <ListItemText>{worktimeAggregationReport.localizedTitle} erstellen ...</ListItemText>
        </MenuItem>
      </Menu>
      {selectedReport && (
        <CreateReportDialog
          defaultValues={{ from: dateRange[0], to: dateRange[1], includePlannedAppointments: true }}
          report={selectedReport}
          {...createReportDialogProps}
        />
      )}
    </Box>
  );
}

function State(props: { label: string; amount: number; unit: { singular: string; plural: string } }) {
  const { label, amount, unit } = props;
  return (
    <Column sx={{ minWidth: 0 }}>
      <Row>
        <Typography variant="body2" color="text.secondary" fontSize="12px">
          {label}
        </Typography>
      </Row>
      <Row sx={{ gap: "4px", alignItems: "baseline", color: "text.primary" }}>
        <Typography variant="h6" fontWeight={600} lineHeight={1.33} fontSize={{ xs: "16px", sm: "19px" }}>
          {isNaN(amount) ? "\u00A0" : _numberFormatter.format(amount)}
        </Typography>
        <Typography variant="subtitle2" fontSize="12px" fontWeight={600}>
          {isNaN(amount) ? "" : amount === 1 ? unit.singular : unit.plural}
        </Typography>
      </Row>
    </Column>
  );
}
